import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useEffect, useMemo, useState } from 'react'

import {
  ensureTrackingWalletsAction,
  trackingWalletsByAddressAtom,
} from '@root/features/wallet-tracker/atoms/reatom-tracking-wallets'

import { laneCount, laneMembers, laneTrades } from '../../model/tape-lanes'
import { shortAddress, since, usd } from '../portfolio-screen/money'

import type { Lane } from '../../model/tape-lanes'
import type { PairModel } from '@root/entities/pair'
import type { Trade } from '@root/entities/trade'

const LANES: ReadonlyArray<{ lane: Lane; label: string }> = [
  { lane: 'all', label: 'All' },
  { lane: 'tracked', label: 'Tracked' },
  { lane: 'dev', label: 'Dev' },
]

/**
 * The tape, narrow, beside the chart.
 *
 * Both terminals we are measured against put one here, and it is not the same
 * table as the wide one underneath: four columns, no header chrome, read with
 * the eyes already on the candles. What it adds that neither of theirs can is
 * the middle lane — the wallets this account follows, which is the whole
 * reason to open our token page instead of one of theirs.
 *
 * All three lanes are real. Dev is the deployer and whoever sent the first
 * transaction, which are usually the same wallet and occasionally are not.
 */
export const SideTape: React.FC<{
  pair: PairModel
  trades: ReadonlyArray<Trade.Trade>
  onFold: () => void
  onMoveBelow: () => void
}> = memo(({ pair, trades, onFold, onMoveBelow }) => {
  const [lane, setLane] = useState<Lane>('all')
  const [tracking] = useAtom(trackingWalletsByAddressAtom)
  const [audit] = useAtom(pair.audit)
  const [initTxCreator] = useAtom(pair.initTxCreator)

  /* The tracker list belongs to the account, not to this screen, and nothing
     else on a token page asks for it. */
  const ensureTracking = useAction(ensureTrackingWalletsAction)
  useEffect(() => ensureTracking(), [ensureTracking])

  const deployer = audit.meta.deployerAddress
  const creator = initTxCreator?.address

  const members = useMemo(
    () =>
      laneMembers({
        tracked: Object.keys(tracking),
        dev: [
          deployer ? String(deployer) : undefined,
          creator ? String(creator) : undefined,
        ],
      }),
    [tracking, deployer, creator],
  )

  const rows = useMemo(
    () =>
      trades.map((trade) => ({
        trade,
        maker: String(trade.maker.address),
      })),
    [trades],
  )

  const shown = useMemo(
    () => laneTrades(rows, lane, members),
    [rows, lane, members],
  )

  return (
    <section className="pn sidetape">
      <header>
        {LANES.map((each) => {
          const count = laneCount(
            laneTrades(rows, each.lane, members).length,
            each.lane,
            members,
          )
          return (
            <button
              key={each.lane}
              className={each.lane === lane ? 'ln on' : 'ln'}
              type="button"
              onClick={() => setLane(each.lane)}
            >
              {each.label}
              {count === undefined ? undefined : (
                <em className="mono">{count}</em>
              )}
            </button>
          )
        })}

        <div className="right">
          <span className="tag live">live</span>
          <button
            className="ib"
            type="button"
            title="Put the tape under the chart instead"
            aria-label="Put the tape under the chart instead"
            onClick={onMoveBelow}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M15 3 L15 21" />
            </svg>
          </button>
          <button
            className="ib"
            type="button"
            title="Hide the tape and give the chart its width back"
            aria-label="Hide the tape and give the chart its width back"
            onClick={onFold}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 6 L9 12 L15 18" />
            </svg>
          </button>
        </div>
      </header>

      <div className="sthead">
        <span>TOTAL</span>
        <span>MC</span>
        <span>TRADER</span>
        <span>AGE</span>
      </div>

      <div className="stbody">
        {shown.length === 0 ? (
          <p className="soon">{emptyLine(lane, members.tracked.size)}</p>
        ) : (
          shown.map(({ trade, maker }) => {
            const buy = trade.type === 'buy'
            return (
              <div
                className={
                  members.tracked.has(maker.toLowerCase()) ? 'st lit' : 'st'
                }
                key={String(trade.id)}
              >
                <span
                  className="mono"
                  style={{ color: buy ? 'var(--up)' : 'var(--down)' }}
                >
                  {usd(trade.totalUsd.toNumber())}
                </span>
                <span className="mono">
                  {usd(trade.marketCapUsd.toNumber())}
                </span>
                <span className="who">
                  {members.tracked.has(maker.toLowerCase()) ? (
                    <svg
                      width="9"
                      height="9"
                      viewBox="0 0 24 24"
                      fill="#93a4ff"
                      aria-label="A wallet you follow"
                    >
                      <path d="M12 3 L14.6 8.7 L21 9.5 L16.4 13.8 L17.6 20 L12 17 L6.4 20 L7.6 13.8 L3 9.5 L9.4 8.7 Z" />
                    </svg>
                  ) : undefined}
                  <a
                    className="mono maker"
                    href={`/portfolio-v2?wallet=${maker}`}
                    title="See what this wallet has been trading"
                  >
                    {shortAddress(maker)}
                  </a>
                </span>
                <span className="mono age">
                  {since(trade.timestamp.getTime())}
                </span>
              </div>
            )
          })
        )}
      </div>
    </section>
  )
})

/**
 * Why a lane is empty, which is never the same reason twice.
 *
 * An empty tracked lane on an account that follows nobody is an invitation;
 * on one that follows forty wallets it is information about this token.
 */
const emptyLine = (lane: Lane, trackedCount: number): string => {
  if (lane === 'tracked') {
    return trackedCount === 0
      ? 'You are not following any wallets yet.'
      : 'None of the wallets you follow has traded this.'
  }
  if (lane === 'dev') {
    return 'The dev has not traded since this page opened.'
  }
  return 'Waiting for the first trade…'
}
